import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';

import { Logger } from '@shared';
import { CredentialsService } from './credentials.service';
import { IpApiService } from '@app/@shared/services/ip-api/ip-api.service';
import { LocationService } from '@app/@shared/services/location.service';
import { LoginModalComponent } from './login-modal.component';

const log = new Logger('AuthenticationGuard');

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  private modalOpen = false;

  constructor(
    private router: Router,
    private credentialsService: CredentialsService,
    private ipApiService: IpApiService,
    private locationS: LocationService,
    private modalCtrl: ModalController
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // Permitir acceso a home y legal sin autenticación
    if (state.url === '/home' || state.url === '/' || state.url.includes('/legal')) {
      return true;
    }

    const platform = Capacitor.getPlatform();
    const isAuthenticated = this.credentialsService.isAuthenticated();
    let isAllowed = platform !== 'web' ? await this.ipApiService.isMx() : true;

    if (isAllowed === null) {
      //opcioines con location sercice
      isAllowed = await this.locationS.isMx();
    }

    // Si está autenticado y permitido, permitir acceso
    if (platform === 'web') {
      if (isAuthenticated) {
        return true;
      }
    } else {
      if (isAuthenticated && isAllowed) {
        return true;
      }
    }

    // Si no está autenticado, mostrar modal de login
    if (!isAuthenticated) {
      log.debug('No autenticado, mostrando modal de login...');

      if (!this.modalOpen) {
        this.modalOpen = true;

        const modal = await this.modalCtrl.create({
          component: LoginModalComponent,
          backdropDismiss: false,
          cssClass: 'login-modal',
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        this.modalOpen = false;

        // Si el login fue exitoso, permitir acceso
        if (data?.success && this.credentialsService.isAuthenticated()) {
          // Verificar nuevamente el país si no es web
          if (platform !== 'web') {
            const isAllowedAfterLogin = await this.ipApiService.isMx();
            if (isAllowedAfterLogin === null) {
              const locationAllowed = await this.locationS.isMx();
              if (!locationAllowed) {
                return false;
              }
            } else if (!isAllowedAfterLogin) {
              return false;
            }
          }
          return true;
        }
      }

      return false;
    }

    // Si está autenticado pero no está permitido (solo para no-web)
    if (!isAllowed && platform !== 'web') {
      log.debug('No se cumplen las condiciones para acceder (país no permitido)...');
      return false;
    }

    return false;
  }
}
